import { useCallback, useEffect, useRef } from 'react';

import { Colors, Button, Flex, PrimaryColors, Text } from '@revenuemarket/cds';
import { mixpanelTrack } from '@revenuemarket/utils/mixpanel';
import CommonModal from '~components/ui/Modal/CommonModal';
import { css } from '~libs/emotion';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';

export const PRIVACY_NOTICE_CHANGE_POPUP_HIDE_KEY = 'privacyNoticeChangePopupHide';
const PRIVACY_NOTICE_CHANGE_URL =
  'https://intercom.help/clobe/ko/articles/13644007-%EA%B0%9C%EC%9D%B8%EC%A0%95%EB%B3%B4-%EC%B2%98%EB%A6%AC%EB%B0%A9%EC%B9%A8-2026-02-13-%EC%8B%9C%ED%96%89';

interface PrivacyNoticeChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyNoticeChangeModal = ({ isOpen, onClose }: PrivacyNoticeChangeModalProps) => {
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      mixpanelTrack(MIXPANEL_EVENT.PRIVACY.OPEN_NOTICE_CHANGE_MODAL);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  const handleClose = useCallback(() => {
    mixpanelTrack(MIXPANEL_EVENT.PRIVACY.CLOSE_NOTICE_CHANGE_MODAL, {
      doNotShowAgain: false,
    });
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    localStorage.setItem(PRIVACY_NOTICE_CHANGE_POPUP_HIDE_KEY, 'true');
    mixpanelTrack(MIXPANEL_EVENT.PRIVACY.CLOSE_NOTICE_CHANGE_MODAL, {
      doNotShowAgain: true,
    });
    onClose();
  }, [onClose]);

  return (
    <CommonModal
      title={
        <Text textStyle="20_600" style={{ color: Colors.gray800 }}>
          개인정보 처리방침 개정 안내
        </Text>
      }
      open={isOpen}
      onCancel={handleClose}
      centered
      footer={null}
      width={540}
    >
      <Flex direction="column" gap={16} style={{ paddingTop: 16 }}>
        <Text textStyle="14_400" style={{ color: Colors.gray700, lineHeight: '22px' }}>
          안녕하세요, 서비스팀입니다. 더욱 정확하고 편리한 데이터 분석 및 서비스 제공을 위하여
          개인정보 처리방침이 개정될 예정입니다.
        </Text>

        <Flex
          direction="column"
          gap={12}
          style={{
            backgroundColor: Colors.gray50,
            padding: 16,
            borderRadius: 8,
          }}
        >
          <Flex direction="column" gap={4}>
            <Text textStyle="14_600" style={{ color: Colors.gray800 }}>
              주요 변경 사항
            </Text>
            <Text textStyle="14_400" style={{ color: Colors.gray700 }}>
              서비스 운영 및 데이터 분석 수탁업체 추가 (헥토데이터, 쿠콘, 스티비 등 총 5개사)
            </Text>
          </Flex>
          <Flex direction="column" gap={4}>
            <Text textStyle="14_600" style={{ color: Colors.gray800 }}>
              시행 예정일
            </Text>
            <Text textStyle="14_400" style={{ color: Colors.gray700 }}>
              2026년 2월 13일 (공지일로부터 7일 후 기준)
            </Text>
          </Flex>
          <Flex direction="column" gap={4}>
            <Text textStyle="14_600" style={{ color: Colors.gray800 }}>
              상세 내용
            </Text>
            <Text textStyle="14_400" style={{ color: Colors.gray700 }}>
              <a
                href={PRIVACY_NOTICE_CHANGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: PrimaryColors.primary500 }}
              >
                개정 전문 보기
              </a>
              를 통해 확인하실 수 있습니다.
            </Text>
          </Flex>
        </Flex>

        <Text textStyle="13_400" style={{ color: Colors.gray600, lineHeight: '20px' }}>
          본 개정에 동의하지 않으시는 경우 고객센터를 통해 이의제기 및 회원 탈퇴를 요청하실 수
          있으며, 시행일 전까지 별도의 의사표시를 하지 않으시는 경우 본 개정에 동의하신 것으로
          간주됩니다.
        </Text>

        <Flex direction="column" gap={8}>
          <Text textStyle="14_600" style={{ color: Colors.gray800 }}>
            개인정보 수집 및 이용 동의 추가 항목
          </Text>
          <table
            className={css`
              width: 100%;
              border-collapse: collapse;
              border: 1px solid ${Colors.gray200};

              th,
              td {
                padding: 12px 16px;
                border: 1px solid ${Colors.gray200};
                text-align: left;
              }

              th {
                background-color: ${Colors.gray50};
                width: 120px;
              }
            `}
          >
            <tbody>
              <tr>
                <th>
                  <Text textStyle="14_600" style={{ color: Colors.gray700 }}>
                    목적
                  </Text>
                </th>
                <td>
                  <Text textStyle="14_400" style={{ color: Colors.gray700 }}>
                    데이터 스크래핑 서비스
                  </Text>
                </td>
              </tr>
              <tr>
                <th>
                  <Text textStyle="14_600" style={{ color: Colors.gray700 }}>
                    수집 항목
                  </Text>
                </th>
                <td>
                  <Text textStyle="14_400" style={{ color: Colors.gray700 }}>
                    공동인증서 및 연동 타사(은행, 카드사 등)의 아이디 및 비밀번호
                  </Text>
                </td>
              </tr>
              <tr>
                <th>
                  <Text textStyle="14_600" style={{ color: Colors.gray700 }}>
                    보유 및 이용기간
                  </Text>
                </th>
                <td>
                  <Text textStyle="14_400" style={{ color: Colors.gray700 }}>
                    회원 탈퇴 시까지
                  </Text>
                </td>
              </tr>
            </tbody>
          </table>
        </Flex>

        <Flex justify="flex-end" style={{ paddingTop: 8 }}>
          <Button buttonType="primary" buttonSize="large" onClick={handleConfirm}>
            확인(다시보지 않기)
          </Button>
        </Flex>
      </Flex>
    </CommonModal>
  );
};

export default PrivacyNoticeChangeModal;
